import { IGlobalState } from '../states/GlobalState';
import { initialGlobalState } from '../states/GlobalStateReducer';
import { UbiverseEdge, UbiverseNode, UbiverseSequence, UbiverseSequenceNote } from '../types';
import { getSupabaseClient } from './supabase';

export interface ProjectRecord {
  id: string;
  name: string;
  updatedAt: string;
}

type ProjectRow = {
  id: string;
  name: string | null;
  bpm: number | null;
  signature_numerator: number | null;
  signature_denominator: number | null;
  repeat_from_bar: number | null;
  repeat_from_beat: number | null;
  repeat_to_bar: number | null;
  repeat_to_beat: number | null;
  editor_quantization_is_active: boolean | null;
  editor_quantization_numerator: number | null;
  editor_quantization_denominator: number | null;
  editor_timeline_zoom: number | null;
  is_playing: boolean | null;
  playhead_time: number | null;
  playhead_position_bar: number | null;
  playhead_position_beat: number | null;
  editor_target_id: string | null;
  updated_at: string;
};

type NodeRow = {
  id: string;
  type: string;
  position_x: number;
  position_y: number;
  data: Record<string, unknown> | null;
};

type EdgeRow = {
  id: string;
  source: string;
  target: string;
  source_handle: string | null;
  target_handle: string | null;
  data: Record<string, unknown> | null;
};

type SequenceRow = {
  id: string;
  is_muted: boolean | null;
};

type SequenceNodeRow = {
  id: string;
  sequence_id: string;
  position: number;
  intensity: number;
};

const sanitizeNodeData = (data: Record<string, unknown> | null | undefined) => {
  if (!data) return {};
  const { state, dispatch, realtimeClient, ...rest } = data;
  return rest;
};

export const loadProjectById = async (projectId: string): Promise<{ project: ProjectRecord; state: IGlobalState } | null> => {
  const supabase = getSupabaseClient();
  if (!supabase) return null;

  const { data: project, error: projectError } = await supabase
    .from('projects')
    .select('id, name, bpm, signature_numerator, signature_denominator, repeat_from_bar, repeat_from_beat, repeat_to_bar, repeat_to_beat, editor_quantization_is_active, editor_quantization_numerator, editor_quantization_denominator, editor_timeline_zoom, is_playing, playhead_time, playhead_position_bar, playhead_position_beat, editor_target_id, updated_at')
    .eq('id', projectId)
    .maybeSingle<ProjectRow>();

  if (projectError) {
    console.error('Failed to load project', projectError.message);
    return null;
  }

  if (!project) return null;

  const [nodesResponse, edgesResponse, sequencesResponse, sequenceNodesResponse] = await Promise.all([
    supabase.from('nodes').select('id, type, position_x, position_y, data').eq('project_id', project.id),
    supabase.from('edges').select('id, source, target, source_handle, target_handle, data').eq('project_id', project.id),
    supabase.from('sequences').select('id, is_muted').eq('project_id', project.id),
    supabase.from('sequence_notes').select('id, sequence_id, position, intensity').eq('project_id', project.id)
  ]);

  if (nodesResponse.error || edgesResponse.error || sequencesResponse.error || sequenceNodesResponse.error) {
    console.error('Failed to load project child records', {
      nodes: nodesResponse.error?.message,
      edges: edgesResponse.error?.message,
      sequences: sequencesResponse.error?.message,
      sequenceNodes: sequenceNodesResponse.error?.message
    });
    return null;
  }

  const sequenceNodesBySequenceId = (sequenceNodesResponse.data || []).reduce<Record<string, UbiverseSequenceNote[]>>((acc, row) => {
    const sequenceNode = row as SequenceNodeRow;
    if (!acc[sequenceNode.sequence_id]) {
      acc[sequenceNode.sequence_id] = [];
    }
    acc[sequenceNode.sequence_id].push({
      id: sequenceNode.id,
      position: Number(sequenceNode.position),
      intensity: Number(sequenceNode.intensity)
    });
    return acc;
  }, {});

  const sequences: UbiverseSequence[] = ((sequencesResponse.data || []) as SequenceRow[]).map((row) => ({
    id: row.id,
    name: row.id,
    isMuted: Boolean(row.is_muted),
    notes: (sequenceNodesBySequenceId[row.id] || []).sort((a, b) => a.position - b.position)
  }));

  const nodes: UbiverseNode[] = ((nodesResponse.data || []) as NodeRow[]).map((row) => ({
    id: row.id,
    type: row.type as UbiverseNode['type'],
    position: {
      x: Number(row.position_x),
      y: Number(row.position_y)
    },
    data: sanitizeNodeData(row.data),
    deletable: true,
    connectable: true
  }));

  const edges: UbiverseEdge[] = ((edgesResponse.data || []) as EdgeRow[]).map((row) => ({
    id: row.id,
    source: row.source,
    target: row.target,
    sourceHandle: row.source_handle || undefined,
    targetHandle: row.target_handle || undefined,
    data: row.data || {}
  }));

  const hydratedState: IGlobalState = {
    ...initialGlobalState,
    control: {
      isPlaying: Boolean(project.is_playing),
      playheadTime: Number(project.playhead_time ?? 0),
      playheadPosition: {
        bar: Number(project.playhead_position_bar ?? 1),
        beat: Number(project.playhead_position_beat ?? 1)
      },
      repeat: {
        isActive: Number(project.repeat_from_bar ?? 1) !== Number(project.repeat_to_bar ?? 9) || Number(project.repeat_from_beat ?? 1) !== Number(project.repeat_to_beat ?? 1),
        from: {
          bar: Number(project.repeat_from_bar ?? 1),
          beat: Number(project.repeat_from_beat ?? 1)
        },
        to: {
          bar: Number(project.repeat_to_bar ?? 9),
          beat: Number(project.repeat_to_beat ?? 1)
        }
      },
      bpm: Number(project.bpm ?? 120),
      signature: {
        numerator: Number(project.signature_numerator ?? 4),
        denominator: Number(project.signature_denominator ?? 4)
      }
    },
    editor: {
      targetId: project.editor_target_id || '',
      quantization: {
        isActive: Boolean(project.editor_quantization_is_active ?? true),
        numerator: Number(project.editor_quantization_numerator ?? 1),
        denominator: Number(project.editor_quantization_denominator ?? 8)
      },
      timeline: {
        zoom: Number(project.editor_timeline_zoom ?? 1)
      }
    },
    nodes,
    edges,
    sequences
  };

  return {
    project: {
      id: project.id,
      name: project.name || 'Untitled project',
      updatedAt: project.updated_at
    },
    state: hydratedState
  };
};

export const loadLatestProjectForUser = async (userId: string): Promise<{ project: ProjectRecord; state: IGlobalState } | null> => {
  const supabase = getSupabaseClient();
  if (!supabase) return null;

  const { data: project, error: projectError } = await supabase
    .from('projects')
    .select('id')
    .eq('user_id', userId)
    .order('updated_at', { ascending: false })
    .limit(1)
    .maybeSingle<{ id: string }>();

  if (projectError) {
    console.error('Failed to load project', projectError.message);
    return null;
  }

  if (!project) return null;

  return loadProjectById(project.id);
};

export const saveProjectForUser = async (params: { userId: string; projectId: string; projectName: string; state: IGlobalState }): Promise<ProjectRecord | null> => {
  const supabase = getSupabaseClient();
  if (!supabase) return null;

  const { userId, projectId, projectName, state } = params;

  const { data: projectRows, error: projectError } = await supabase
    .from('projects')
    .upsert({
      id: projectId,
      user_id: userId,
      name: projectName,
      bpm: state.control.bpm,
      signature_numerator: state.control.signature.numerator,
      signature_denominator: state.control.signature.denominator,
      repeat_from_bar: state.control.repeat.from.bar,
      repeat_from_beat: state.control.repeat.from.beat,
      repeat_to_bar: state.control.repeat.to.bar,
      repeat_to_beat: state.control.repeat.to.beat,
      editor_quantization_is_active: state.editor.quantization.isActive,
      editor_quantization_numerator: state.editor.quantization.numerator,
      editor_quantization_denominator: state.editor.quantization.denominator,
      editor_timeline_zoom: state.editor.timeline.zoom,
      is_playing: state.control.isPlaying,
      playhead_time: state.control.playheadTime,
      playhead_position_bar: state.control.playheadPosition.bar,
      playhead_position_beat: state.control.playheadPosition.beat,
      editor_target_id: state.editor.targetId || '',
      updated_at: new Date().toISOString()
    }, { onConflict: 'id' })
    .select('id, name, updated_at')
    .limit(1);

  if (projectError || !projectRows?.length) {
    console.error('Failed to save project', projectError?.message);
    return null;
  }

  const childTables = ['sequence_notes', 'sequences', 'edges', 'nodes'];
  for (const table of childTables) {
    const { error } = await supabase.from(table).delete().eq('project_id', projectId);
    if (error) {
      console.error(`Failed to clear ${table}`, error.message);
      return null;
    }
  }

  const nodes = state.nodes.map((node) => ({
    id: node.id,
    project_id: projectId,
    type: node.type,
    position_x: node.position.x,
    position_y: node.position.y,
    data: sanitizeNodeData(node.data as Record<string, unknown>)
  }));

  const edges = state.edges.map((edge, index) => ({
    id: edge.id || `edge-${index}-${edge.source}-${edge.target}`,
    project_id: projectId,
    source: edge.source,
    target: edge.target,
    source_handle: edge.sourceHandle || null,
    target_handle: edge.targetHandle || null,
    data: edge.data || {}
  }));

  const sequences = state.sequences.map((sequence) => ({
    id: sequence.id,
    project_id: projectId,
    is_muted: sequence.isMuted
  }));

  const sequenceNodes = state.sequences.flatMap((sequence) =>
    sequence.notes.map((note, index) => ({
      id: note.id || `${sequence.id}-note-${index}`,
      sequence_id: sequence.id,
      project_id: projectId,
      position: note.position,
      intensity: note.intensity
    }))
  );

  if (nodes.length > 0) {
    const { error } = await supabase.from('nodes').insert(nodes);
    if (error) {
      console.error('Failed to save nodes', error.message);
      return null;
    }
  }

  if (edges.length > 0) {
    const { error } = await supabase.from('edges').insert(edges);
    if (error) {
      console.error('Failed to save edges', error.message);
      return null;
    }
  }

  if (sequences.length > 0) {
    const { error } = await supabase.from('sequences').insert(sequences);
    if (error) {
      console.error('Failed to save sequences', error.message);
      return null;
    }
  }

  if (sequenceNodes.length > 0) {
    const { error } = await supabase.from('sequence_notes').insert(sequenceNodes);
    if (error) {
      console.error('Failed to save sequence notes', error.message);
      return null;
    }
  }

  const saved = projectRows[0] as { id: string; name: string; updated_at: string };
  return {
    id: saved.id,
    name: saved.name,
    updatedAt: saved.updated_at
  };
};
